import UploadArea from './UploadArea';
import Cropper from 'react-easy-crop';
import { useConfig } from '../../ConfigContext';
import { ScaleLoader } from 'react-spinners';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const CropperComponent = ({
  itemObject, // očekává: { imageSrc, crop:{x,y} | null, zoom:number | null, croppedAreaPixels | null }
  overlayWidth,
  overlayHeight,
  onCropChangeFunction,
  onCropCompleteFunction,
  onZoomChangeFunction,
  isRounded = false,
  isHeart = false,
}) => {
  const { setGeneralState, general } = useConfig();

  // --- HYDRATACE ULOŽENÝCH HODNOT ------------------------------------------
  // Uložené hodnoty dorazí z localStorage do kontextu asynchronně.
  // Potřebujeme je mít připravené *před* mountem Cropperu, aby vzal initialCroppedAreaPixels.
  const [configReady, setConfigReady] = useState(false);
  const [cropperKey, setCropperKey] = useState(0); // změna klíče => remount Cropperu

  // Detekce, že máme k dispozici hodnoty (aspoň zoom a crop; area je volitelná)
  useEffect(() => {
    if (configReady) return;
    const hasImg = !!itemObject?.imageSrc;
    const hasZoom = typeof itemObject?.zoom === 'number';
    const hasCrop =
      itemObject?.crop &&
      typeof itemObject.crop.x === 'number' &&
      typeof itemObject.crop.y === 'number';

    // Jakmile máme obrázek + (zoom & crop), považujeme konfiguraci za připravenou
    if (hasImg && hasZoom && hasCrop) {
      // Remountni Cropper přes key, aby si vzal initialCroppedAreaPixels
      setConfigReady(true);
      setCropperKey((prev) => prev + 1);
    }
  }, [
    itemObject?.imageSrc,
    itemObject?.zoom,
    itemObject?.crop?.x,
    itemObject?.crop?.y,
  ]);

  // Během hydratace ignorujeme první eventy, aby nevracely minZoom apod.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (configReady) {
      // Dáme jeden tick na mount a pak povolíme eventy
      const t = setTimeout(() => {
        hydratedRef.current = true;
      }, 0);
      return () => clearTimeout(t);
    }
  }, [configReady, cropperKey]);

  // Bezpečné hodnoty do Cropperu – už žádné null
  const safeZoom = typeof itemObject?.zoom === 'number' ? itemObject.zoom : 1;
  const safeCrop =
    itemObject?.crop &&
    typeof itemObject.crop.x === 'number' &&
    typeof itemObject.crop.y === 'number'
      ? itemObject.crop
      : { x: 0, y: 0 };

  // initialCroppedAreaPixels ber jen při mountu (props je pouze pro první render)
  const initialCroppedAreaPixels = general.cartKey
    ? general.croppedAreaPixels
    : itemObject?.croppedAreaPixels || null;

  // Volitelné: pokud chceš spočítat fitZoom při prvním načtení obrázku, tak jen v případě,
  // že zoom neexistuje. Tady ale zoom existuje (hydratujeme), takže onMediaLoaded nic nedělá.
  const handleMediaLoaded = useCallback(
    (m) => {
      if (typeof itemObject?.zoom === 'number') return; // už máme uložený zoom
      // … případně sem můžeš dát výpočet fitZoom, když nikdo nic neposlal
      const portrait = m.naturalHeight > m.naturalWidth;
      const fitZoom =
        (overlayWidth * m.naturalHeight) / (overlayHeight * m.naturalWidth);
      const landscapeZoom =
        overlayHeight /
        m.naturalHeight /
        Math.min(
          overlayWidth / m.naturalWidth,
          overlayHeight / m.naturalHeight
        );
      // Guard against min/max constraints; tweak to taste
      onZoomChangeFunction(portrait ? fitZoom : landscapeZoom);
    },
    [itemObject?.zoom, overlayWidth, overlayHeight, onZoomChangeFunction]
  );

  // Pokud není obrázek, renderuj upload
  if (!itemObject?.imageSrc) {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <UploadArea callback={(url) => setGeneralState({ imageSrc: url })} />
      </div>
    );
  }

  // Počkej na konfiguraci, ať se `initialCroppedAreaPixels` vezme při mountu
  if (!configReady) {
    return (
      <div
        style={{
          display: 'grid',
          placeItems: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <span className="loader"></span>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <Cropper
        key={cropperKey} // FORCED REMOUNT, aby se použil initialCroppedAreaPixels
        image={itemObject.imageSrc}
        crop={safeCrop}
        zoom={safeZoom}
        minZoom={0.1}
        // maxZoom={5}                     // volitelně
        showGrid={false}
        cropSize={{ width: overlayWidth - 5, height: overlayHeight - 5 }}
        initialCroppedAreaPixels={initialCroppedAreaPixels || undefined}
        cropShape={isRounded ? 'round' : 'rect'}
        objectFit="contain"
        onMediaLoaded={handleMediaLoaded}
        onCropChange={(c) => {
          // ignoruj eventy během hydratace
          if (!hydratedRef.current) return;
          onCropChangeFunction(c);
        }}
        onZoomChange={(z) => {
          if (!hydratedRef.current) return;
          onZoomChangeFunction(z);
        }}
        onCropComplete={(area, areaPixels) => {
          // Tohle můžeš používat normálně – uloží zpět přesně to, co vidíš
          onCropCompleteFunction(area, areaPixels);
        }}
        style={{ borderRadius: isRounded ? '100%' : '0' }}
      />
    </div>
  );
};

export default CropperComponent;
